/**
 * Responsibility: Implement companions behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import type { GameState, Companion, AttributeKey } from "@anyquest/shared-core"
import { CompanionEntity } from './entities'
// ============================================================
// COMPANIONS.TS — Companion party management
// Companions share LivingEntityStats with the player Vessel.
// All mutations return new GameState (immutable update pattern).
// ============================================================

export function addCompanion(state: GameState, companion: Companion): GameState {
  return {
    ...state,
    companions: [...state.companions, companion],
  }
}

export function removeCompanion(state: GameState, companionName: string): GameState {
  return {
    ...state,
    companions: state.companions.filter(c => c.name !== companionName),
  }
}

/** Apply a vital delta to a companion while respecting that vital's max. */
export function applyCompanionVitalDelta(
  companion: Companion,
  field: 'vitality' | 'spirit' | 'essence',
  delta: number,
): void {
  CompanionEntity.fromDTO(companion).applyVitalDelta(field, delta)
}

/** Prune expired modifiers from a companion. */
export function pruneCompanionModifiers(companion: Companion, totalGameMinutes: number): void {
  CompanionEntity.fromDTO(companion).pruneExpiredModifiers(totalGameMinutes)
}

/** Derived stat for a companion: base attribute + sum of active modifiers. */
export function calcCompanionStat(companion: Companion, key: AttributeKey): number {
  return CompanionEntity.fromDTO(companion).calcStat(key)
}

/** True if the companion is conscious and able to act. */
export function isCompanionActive(companion: Companion): boolean {
  return CompanionEntity.fromDTO(companion).isActive
}

export function isCompanionPresent(companion: Companion, playerLocation: { x: number; y: number }): boolean {
  if (!companion.separated) return true
  if (!companion.location) return true
  return companion.location.x === playerLocation.x && companion.location.y === playerLocation.y
}

export function separateCompanion(companion: Companion, location: { x: number; y: number }): void {
  companion.location = { ...location }
  companion.separated = true
}

export function reuniteCompanion(companion: Companion): void {
  companion.location = undefined
  companion.separated = false
}

export function getPresentCompanions(state: GameState): Companion[] {
  return (state.companions ?? []).filter(c => isCompanionPresent(c, state.player.location))
}
